import {
	Button,
	CustomEmailField,
	CustomTextField,
	TiptapEditor,
} from "@dune/ui";
import { FileText, Send } from "lucide-react";
import { Controller } from "react-hook-form";
import type { Control } from "react-hook-form";
import type {
	EmailFormValues,
	LocalAttachment,
	UseEmailComposerReturn,
} from "../../hooks/useEmailComposer";
import { AttachmentHandler } from "./AttachmentHandler";
import type { AttachableDocument } from "@dune/types";

interface EmailComposerFormProps {
	control: Control<EmailFormValues>;
	watch: UseEmailComposerReturn["watch"];
	disabled?: boolean;
	attachments: LocalAttachment[];
	showCcBcc: boolean;
	setShowCcBcc: React.Dispatch<React.SetStateAction<boolean>>;
	toSearch: UseEmailComposerReturn["toSearch"];
	ccSearch: UseEmailComposerReturn["ccSearch"];
	bccSearch: UseEmailComposerReturn["bccSearch"];
	getHandleEmailSearch: UseEmailComposerReturn["getHandleEmailSearch"];
	handleAttachmentAdd: UseEmailComposerReturn["handleAttachmentAdd"];
	handleAttachmentRemove: UseEmailComposerReturn["handleAttachmentRemove"];
	existingDocuments?: AttachableDocument[];
	isLoadingDocuments?: boolean;
	onExistingDocumentsAdd?: (documents: AttachableDocument[]) => Promise<void>;
	onImageUpload?: (file: File) => Promise<string>;
	isLoadingTemplates?: boolean;
	onTemplateButtonClick?: () => void;
	onSignatureButtonClick?: () => void;
	onSendClick: () => void;
	sendEmailMutation: UseEmailComposerReturn["sendEmailMutation"];
	renderExtraContent?: () => React.ReactNode;
}

export const EmailComposerForm: React.FC<EmailComposerFormProps> = ({
	control,
	watch,
	disabled = false,
	attachments,
	showCcBcc,
	setShowCcBcc,
	toSearch,
	ccSearch,
	bccSearch,
	getHandleEmailSearch,
	handleAttachmentAdd,
	handleAttachmentRemove,
	existingDocuments = [],
	isLoadingDocuments = false,
	onExistingDocumentsAdd,
	onImageUpload,
	isLoadingTemplates = false,
	onTemplateButtonClick,
	onSignatureButtonClick,
	onSendClick,
	sendEmailMutation,
	renderExtraContent,
}) => {
	const toRecipients = watch("to");

	return (
		<div className="flex flex-col h-full">
			<div className="space-y-5">
				<div className="space-y-2">
					<Controller
						name="to"
						control={control}
						render={({ field }) => (
							<CustomEmailField
								label="À"
								value={field.value}
								onValueChange={field.onChange}
								onSearch={getHandleEmailSearch("to")}
								disabled={disabled}
								isSearching={toSearch.isLoading}
								defaultOptions={toSearch.results}
								triggerSearchOnFocus={true}
							/>
						)}
					/>
					{!showCcBcc && (
						<div className="flex items-center">
							<Button
								variant="link"
								size="sm"
								onClick={() => setShowCcBcc(true)}
								className="text-xs text-muted-foreground p-0 h-auto flex items-center gap-1.5"
							>
								<span className="text-primary">+</span> Ajouter des
								destinataires Cc/Cci
							</Button>
						</div>
					)}
				</div>

				{showCcBcc && (
					<Controller
						name="cc"
						control={control}
						render={({ field }) => (
							<CustomEmailField
								label="Cc"
								value={field.value}
								onValueChange={field.onChange}
								onSearch={getHandleEmailSearch("cc")}
								disabled={disabled}
								isSearching={ccSearch.isLoading}
								defaultOptions={ccSearch.results}
							/>
						)}
					/>
				)}

				{showCcBcc && (
					<Controller
						name="bcc"
						control={control}
						render={({ field }) => (
							<CustomEmailField
								label="Cci"
								value={field.value}
								onValueChange={field.onChange}
								onSearch={getHandleEmailSearch("bcc")}
								disabled={disabled}
								isSearching={bccSearch.isLoading}
								defaultOptions={bccSearch.results}
							/>
						)}
					/>
				)}

				<Controller
					name="subject"
					control={control}
					render={({ field }) => (
						<CustomTextField
							label="Objet"
							value={field.value}
							onChange={(e) => field.onChange(e.target.value)}
							disabled={disabled}
						/>
					)}
				/>

				<Controller
					name="content"
					control={control}
					render={({ field }) => (
						<TiptapEditor
							content={field.value}
							onUpdate={(newContent) => field.onChange(newContent)}
							disabled={disabled}
							size="medium"
							onImageUpload={onImageUpload}
						/>
					)}
				/>
			</div>

			<div className="mt-5">
				{onTemplateButtonClick && (
					<div className="mb-4 space-y-2">
						<Button
							variant="outline"
							onClick={onTemplateButtonClick}
							disabled={disabled || isLoadingTemplates}
						>
							<FileText className="w-4 h-4 mr-2" />
							Utiliser un template
						</Button>
						{renderExtraContent?.()}
					</div>
				)}
				<AttachmentHandler
					attachments={attachments}
					onAttachmentAdd={handleAttachmentAdd}
					onAttachmentRemove={handleAttachmentRemove}
					existingDocuments={existingDocuments}
					isLoadingDocuments={isLoadingDocuments}
					onExistingDocumentsAdd={onExistingDocumentsAdd}
				/>
				{onSignatureButtonClick && (
					<div className="mt-2">
						<Button
							variant="link"
							size="sm"
							onClick={onSignatureButtonClick}
							className="p-0 h-auto text-xs text-muted-foreground"
						>
							Modifier la signature
						</Button>
					</div>
				)}
			</div>

			<div className="mt-6 flex justify-end">
				<Button
					onClick={onSendClick}
					className="flex items-center gap-2"
					disabled={
						disabled ||
						sendEmailMutation.isPending ||
						toRecipients.length + watch("cc").length + watch("bcc").length ===
							0 ||
						attachments.some((att) => att.isUploading)
					}
					isLoading={sendEmailMutation.isPending}
				>
					<Send size={16} />
					{sendEmailMutation.isPending ? "Envoi en cours..." : "Envoyer"}
				</Button>
			</div>
		</div>
	);
};
